import * as loadingIndicator from './loadingIndicator'

const SESSION_STORAGE_KEY_FOR_SCROLL_POSITION = 'scroll-position-marker'
const PAGINATION_LOADER_PREV_SELECTOR = '.pagination-loader-prev'
const PAGINATION_LOADER_NEXT_SELECTOR = '.pagination-loader-next'
const PAGINATION_ANCHOR_PREV_SELECTOR = '.pagination-anchor-prev'
const PAGINATION_ANCHOR_NEXT_SELECTOR = '.pagination-anchor-next'
const INTERSECTION_OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0.9,
}

export default function infiniteScroll(options) {
  options = options || {}

  const done = options.done
  const feedElementSelector = options.rootSelector || '.gh-feed'

  const feedElement = document.querySelector(feedElementSelector)
  if (!feedElement) return

  initScrollRestoration(feedElement)

  const LOADED_PAGE_URLS = {
    [window.location.pathname]: document.title,
  }

  const urlChangeObserver = new IntersectionObserver(function (entries) {
    const intersectingEntries = entries.filter(function (entry) {
      return entry.isIntersecting
    })

    if (!intersectingEntries.length) return

    try {
      changeUrlForIntersecting(intersectingEntries)
    } catch (err) {
      // show error to users??
    }
  }, INTERSECTION_OBSERVER_OPTIONS)

  const paginationLoaderObserver = new IntersectionObserver(function (
    entries,
    observerInstance
  ) {
    if (isLoadingNext || isLoadingPrev) return

    const intersectingEntryWithNotFetchedUrl = entries.find(function (entry) {
      const href = entry.target.dataset.url

      return (
        entry.isIntersecting && !!href && LOADED_PAGE_URLS[href] === undefined
      )
    })

    if (!intersectingEntryWithNotFetchedUrl) return

    try {
      paginateForIntersectedEntry(
        intersectingEntryWithNotFetchedUrl,
        observerInstance
      )
    } catch (err) {
      // show error to users??
    }
  }, INTERSECTION_OBSERVER_OPTIONS)

  const initialPreviousLoaderElement = document.querySelector(
    PAGINATION_LOADER_PREV_SELECTOR
  )
  const initialNextLoaderElement = document.querySelector(
    PAGINATION_LOADER_NEXT_SELECTOR
  )
  const firstPrevPaginationAnchorElement = document.querySelector(
    PAGINATION_ANCHOR_PREV_SELECTOR
  )
  const firstNextPaginationAnchorElement = document.querySelector(
    PAGINATION_ANCHOR_NEXT_SELECTOR
  )

  firstPrevPaginationAnchorElement &&
    urlChangeObserver.observe(firstPrevPaginationAnchorElement)
  firstNextPaginationAnchorElement &&
    urlChangeObserver.observe(firstNextPaginationAnchorElement)

  initialPreviousLoaderElement &&
    paginationLoaderObserver.observe(initialPreviousLoaderElement)
  initialNextLoaderElement &&
    paginationLoaderObserver.observe(initialNextLoaderElement)

  let isLoadingNext = false
  let isLoadingPrev = false

  async function paginateForIntersectedEntry(
    intersectedEntry,
    observerInstance
  ) {
    const el = intersectedEntry.target
    const hrefToLoad = el.dataset.url
    const isPrev = el.dataset.type === 'prev'
    const isNext = el.dataset.type === 'next'

    isLoadingPrev = isPrev
    isLoadingNext = isNext

    observerInstance.unobserve(el)

    loadingIndicator.show()

    const res = await fetch(hrefToLoad)
    const html = await res.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    LOADED_PAGE_URLS[hrefToLoad] = doc.title

    const previousLoaderElement = doc.querySelector(
      PAGINATION_LOADER_PREV_SELECTOR
    )
    const nextLoaderElement = doc.querySelector(PAGINATION_LOADER_NEXT_SELECTOR)
    const featuredFeed = doc.querySelector('.gh-featured')
    const postElements = doc.querySelectorAll(
      '.gh-feed:not(.gh-featured):not(.gh-related) > *'
    )
    const fragment = document.createDocumentFragment()
    const elements = []

    const postElementsArray = [...postElements]

    postElementsArray.forEach(function (el) {
      const clonedItem = document.importNode(el, true)

      clonedItem.style.visibility = 'hidden'
      fragment.appendChild(clonedItem)
      elements.push(clonedItem)
    })

    !isPrev ? feedElement.appendChild(fragment) : feedElement.prepend(fragment)
    featuredFeed &&
      feedElement.parentNode.insertBefore(featuredFeed, feedElement)

    if (isPrev && initialPreviousLoaderElement && previousLoaderElement) {
      const url = previousLoaderElement.dataset.url || ''
      const page = previousLoaderElement.dataset.page || ''
      initialPreviousLoaderElement.setAttribute('data-url', url)
      initialPreviousLoaderElement.setAttribute('data-page', page)
    }

    if (!isPrev && initialNextLoaderElement && nextLoaderElement) {
      const url = nextLoaderElement.dataset.url || ''
      const page = nextLoaderElement.dataset.page || ''
      initialNextLoaderElement.setAttribute('data-url', url)
      initialNextLoaderElement.setAttribute('data-page', page)
    }

    function onComplete() {
      loadingIndicator.hide()

      if (isPrev) {
        isLoadingPrev = false
      } else {
        isLoadingNext = false
      }

      const urlChangePrevElementToObserve = elements.find(function (el) {
        return el.dataset.type === 'prev'
      })
      const urlChangeNextElementToObserve = elements.find(function (el) {
        return el.dataset.type === 'next'
      })

      urlChangePrevElementToObserve &&
        urlChangeObserver.observe(urlChangePrevElementToObserve)
      urlChangeNextElementToObserve &&
        urlChangeObserver.observe(urlChangeNextElementToObserve)

      observerInstance.observe(el)
      restoreScroll(feedElement)
    }

    if (done) {
      done({ items: elements, featuredFeed, onComplete, isPrev, isNext })
    } else {
      onComplete()
    }
  }

  function changeUrlForIntersecting(intersectingEntries) {
    const entry = getIntersectingEntryWithTheHighestRatio(intersectingEntries)
    const href = entry.target.dataset.url
    const title = LOADED_PAGE_URLS[href]

    if (href) window.history.replaceState(null, '', href)
    if (title) document.title = title
  }
}

let lastRestoredScrollEl = null
let hasUserScrolled = false

function initScrollRestoration(grid) {
  restoreScroll(grid)

  function setUserHasScrolled() {
    hasUserScrolled = true
  }

  grid.addEventListener('click', function (e) {
    const postEl = e.target.closest('a.post-link[href]')
    const href = postEl && postEl.getAttribute('href')

    if (href) {
      persistClickedItemForScrollRestore(`a.post-link[href="${href}"]`)
    }
  })

  window.addEventListener('wheel', setUserHasScrolled)
  window.addEventListener('touchmove', setUserHasScrolled)
}

function restoreScroll(rootEl) {
  try {
    const key = getSessionKeyForScrollRestore()
    const storedValue = window.sessionStorage.getItem(key)

    let el = storedValue ? rootEl.querySelector(storedValue) : null
    if (!el && lastRestoredScrollEl && !hasUserScrolled) {
      el = lastRestoredScrollEl
    }

    if (el) {
      lastRestoredScrollEl = el
      window.sessionStorage.removeItem(key)
      el.scrollIntoView({ behavior: 'auto', block: 'center' })
    }
  } catch (err) {
    // show error to users??
  }
}

function getIntersectingEntryWithTheHighestRatio(intersectingEntries) {
  let intersectingEntryWithTheHighestRatio = intersectingEntries[0]

  intersectingEntries.forEach(function (entry) {
    if (
      entry.intersectionRatio >
      intersectingEntryWithTheHighestRatio.intersectionRatio
    ) {
      intersectingEntryWithTheHighestRatio = entry
    }

    if (
      entry.intersectionRatio ===
      intersectingEntryWithTheHighestRatio.intersectionRatio
    ) {
      const pageNumberOfTheEntry = parseInt(entry.target.dataset.page, 10)
      const pageNumberOfTheExistingEntry = parseInt(
        intersectingEntryWithTheHighestRatio.target.dataset.page
      )

      if (pageNumberOfTheEntry < pageNumberOfTheExistingEntry) {
        intersectingEntryWithTheHighestRatio = entry
      }
    }
  })

  return intersectingEntryWithTheHighestRatio
}

function getSessionKeyForScrollRestore() {
  const bodyClassName = document.body.classList.value.split(' ').join('_')
  return `${SESSION_STORAGE_KEY_FOR_SCROLL_POSITION}__${bodyClassName}`
}

function persistClickedItemForScrollRestore(selector) {
  try {
    window.sessionStorage.setItem(getSessionKeyForScrollRestore(), selector)
  } catch (err) {
    // show error to users??
  }
}
